import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Route as RouteIcon, ZoomOutMap as ZoomOutMapIcon } from "@mui/icons-material";
import { GoogleMap, Libraries, useLoadScript } from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shop } from "../../../../types/shop";
import { useAppSelector } from "../../../../app/hooks";
import { LoadingSpinner } from "../../../../components/common/LoadingSpinner";
import { useGetShopsQuery } from "../../../../api/shopsApi";
import {
  Coordinate,
  MapPanOptions,
  panToCoordinate,
  panToRegion,
  flyToCoordinates,
} from "../../../../utils/mapUtils";
import { useFullscreen } from "../../hooks/map/useFullscreen";
import { useMapType } from "../../hooks/map/useMapType";
import { useMapZoom } from "../../hooks/map/useMapZoom";
import { useShopMarkers } from "../../hooks/map/useShopMarkers";
import { useTrafficLayer } from "../../hooks/map/useTrafficLayer";
import { useVehicleHistory } from "../../hooks/map/useVehicleHistory";
import { useVehicleMarkers } from "../../hooks/map/useVehicleMarkers";
import { useClosestShops } from "../../hooks/useClosestShops";
import { useHosProfiles } from "../../hooks/useHosProfiles";
import { useVehicles } from "../../hooks/useVehicles";
import { useGetAnyTrackLocationsQuery } from "../../../../api/anyTrackApi";
import { useAnyTrackMarkers } from "../../hooks/map/useAnyTrackMarkers";
import AnyTrackSearchControl from "./controls/AnyTrackSearchControl";
import { MapOptions, MapType, WeatherLayerOption } from "../../types/map/types";
import { VehicleSidebar } from "../sidebar/VehicleSidebar";
import { TrailerSidebar } from "../sidebar/TrailerSidebar";
import MapControls from "./controls/MapControls";
import ShopSearchControl from "./controls/ShopSearchControl";
import { MapTooltipsRow } from "./MapTooltipsRow";
import { TrailerTooltip } from "./TrailerTooltip";
import SignalRIndicator from "./SignalRIndicator";
import { useSignalR } from "../../../../contexts/SignalRContext";
import { TimeZonesOverlay } from "./TimeZonesOverlay";
import ViolationsOverlay from "./ViolationsOverlay";
import { WeatherOverlay } from "./WeatherOverlay";

// Define libraries as a static constant
const GOOGLE_MAPS_LIBRARIES: Libraries = ["marker", "core", "places"];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = {
  lat: 39.8283, // Center of USA
  lng: -98.5795,
};

const defaultZoom = 5;
// Close enough to see the vehicle in context, without street-level zoom (15–16).
const vehicleFocusZoom = 12;

const defaultWeatherLayers: WeatherLayerOption[] = [
  { type: "precipitation", isVisible: false },
  { type: "clouds", isVisible: false },
  { type: "temperature", isVisible: false },
  { type: "wind", isVisible: false },
  { type: "pressure", isVisible: false },
];

const defaultMapOptions: MapOptions = {
  currentMapType: "roadmap",
  isFullscreen: false,
  showClusters: false,
  showVehicles: true,
  showShops: false,
  showTraffic: false,
  showTimeZones: false,
  showRoute: false,
  showAnyTrackVehicles: false,
  weatherLayers: defaultWeatherLayers,
  zoomSensitivity: 1.4,
};

const FleetMap = () => {
  const theme = useTheme();
  const isLaptopScreen = useMediaQuery(theme.breakpoints.down("xl"), {
    noSsr: true,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [key, setKey] = useState(0);
  const [shopSearchQuery, setShopSearchQuery] = useState("");
  const [anyTrackSearchQuery, setAnyTrackSearchQuery] = useState("");
  const [googleShops, setGoogleShops] = useState<Shop[]>([]);

  // Reagujemo na promenu teme sa force re-render
  useEffect(() => {
    setKey((prev) => prev + 1);
  }, [theme.palette.mode]);

  const [mapOptions, setMapOptions] = useState<MapOptions>(defaultMapOptions);

  const { setMapType } = useMapType({ map });
  const { toggleFullscreen } = useFullscreen({ map });
  useMapZoom({
    map,
    zoomSensitivity: mapOptions.zoomSensitivity,
  });
  const token = useAppSelector((state) => state.auth.token);

  const {
    vehicles,
    isLoading: isVehiclesLoading,
    error: vehiclesError,
  } = useVehicles();
  const { data: allShops = [] } = useGetShopsQuery(undefined);

  const { profiles } = useHosProfiles();

  const [selectedVehicleId, setSelectedVehicleId] = useState<number>();
  const [selectedShopId, setSelectedShopId] = useState<number>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isLaptopScreen);
  const [isTrailerSidebarOpen, setIsTrailerSidebarOpen] = useState(!isLaptopScreen);
  const [selectedTrailerId, setSelectedTrailerId] = useState<string>();
  const [showTooltips, setShowTooltips] = useState(true);
  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);
  const selectedHosProfile = selectedVehicle
    ? profiles.find((p) => p.vehicleId === selectedVehicle.id) ?? null
    : null;

  const {
    isConnected,
    subscribeVehicleFocus,
    unsubscribeVehicleFocus,
  } = useSignalR();

  // Subscribe to 10s focused location updates while a vehicle is selected
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    if (selectedVehicleId == null) {
      void unsubscribeVehicleFocus();
      return;
    }

    void subscribeVehicleFocus(selectedVehicleId);

    return () => {
      void unsubscribeVehicleFocus();
    };
  }, [
    selectedVehicleId,
    isConnected,
    subscribeVehicleFocus,
    unsubscribeVehicleFocus,
  ]);

  // Get closest shops by aerial distance (20 shops, then distance matrix for top 10)
  const {
    closestShops,
    isLoading: isShopsLoading,
    isCalculatingDriving,
    error: shopsError,
  } = useClosestShops(selectedVehicle, 20);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: "beta",
  });

  const handleVehicleClick = useCallback(
    (vehicleId: number) => {
      setSelectedVehicleId(vehicleId);
      setSelectedTrailerId(undefined);
      setShowTooltips(true); // Show tooltips when vehicle is selected
      setMapOptions((prev) => ({ ...prev, showRoute: false }));

      const vehicle = vehicles.find((v) => v.id === vehicleId);
      if (vehicle?.lastLatitude && vehicle?.lastLongitude && map) {
        panToCoordinate(
          map,
          {
            latitude: vehicle.lastLatitude,
            longitude: vehicle.lastLongitude,
          },
          vehicleFocusZoom
        );
      }
    },
    [map, vehicles]
  );

  const handleShowAllVehicles = useCallback(() => {
    if (!map) {
      return;
    }

    const coordinates: Coordinate[] = vehicles
      .filter((vehicle) => vehicle.lastLatitude && vehicle.lastLongitude)
      .map((vehicle) => ({
        latitude: vehicle.lastLatitude as number,
        longitude: vehicle.lastLongitude as number,
      }));

    flyToCoordinates(map, coordinates, {
      padding: 80,
      maxZoom: 8,
      fallback: {
        center: { latitude: defaultCenter.lat, longitude: defaultCenter.lng },
        zoom: defaultZoom,
      },
    });
  }, [map, vehicles]);

  const handleCloseTooltips = useCallback(() => {
    setShowTooltips(false);
    setSelectedVehicleId(undefined);
    setGoogleShops([]);
    setMapOptions((prev) => ({ ...prev, showRoute: false }));
    handleShowAllVehicles();
  }, [handleShowAllVehicles]);

  const handleGoogleShopSelect = useCallback((shops: Shop | Shop[]) => {
    console.log("🏪 Google shop(s) selected:", shops);
    const shopArray = Array.isArray(shops) ? shops : [shops];
    setGoogleShops(shopArray); // Add selected shop(s) to map
  }, []);

  const handleShopClick = useCallback(
    (shop: Shop) => {
      console.log("🏪 Shop clicked:", shop);
      setSelectedShopId(shop.id);
      if (shop.latitude && shop.longitude && map) {
        map.setCenter({ lat: shop.latitude, lng: shop.longitude });
        map.setZoom(15);
      }
    },
    [map]
  );

  // Map panning methods
  const panToCoordinates = useCallback(
    (coordinates: Coordinate[], options?: MapPanOptions) => {
      if (!map) {
        console.warn("Map not available for panning");
        return;
      }
      console.log("🗺️ Panning to coordinates:", coordinates);
      panToRegion(map, coordinates, options);
    },
    [map]
  );

  const panToSingleCoordinate = useCallback(
    (coordinate: Coordinate, zoom?: number) => {
      if (!map) {
        console.warn("Map not available for panning");
        return;
      }
      console.log("🗺️ Panning to coordinate:", coordinate, "with zoom:", zoom);
      panToCoordinate(map, coordinate, zoom);
    },
    [map]
  );

  const panToShops = useCallback(
    (shops: Shop[], options?: MapPanOptions) => {
      if (!map || shops.length === 0) {
        console.warn("Map not available or no shops provided for panning");
        return;
      }

      const coordinates: Coordinate[] = shops
        .filter((shop) => shop.latitude && shop.longitude)
        .map((shop) => ({
          latitude: shop.latitude,
          longitude: shop.longitude,
        }));

      if (coordinates.length > 0) {
        console.log("🏪 Panning to shops:", shops.length);
        panToRegion(map, coordinates, options);
      }
    },
    [map]
  );

  // Pan to closest shops
  const handlePanToShops = useCallback(() => {
    if (!closestShops || closestShops.length === 0) {
      console.warn("No closest shops available for panning");
      return;
    }

    const shopCoordinates: Coordinate[] = closestShops.map((shop) => ({
      latitude: shop.shop.latitude,
      longitude: shop.shop.longitude,
    }));

    // Include vehicle location if available
    if (selectedVehicle?.lastLatitude && selectedVehicle?.lastLongitude) {
      shopCoordinates.push({
        latitude: selectedVehicle.lastLatitude,
        longitude: selectedVehicle.lastLongitude,
      });
    }

    panToCoordinates(shopCoordinates, { padding: 100, maxZoom: 15 });
  }, [closestShops, selectedVehicle, panToCoordinates]);

  // Center on a specific shop
  const handleCenterShop = useCallback(
    (shop: any) => {
      console.log("🎯 handleCenterShop called with shop:", shop);

      if (!shop.latitude || !shop.longitude) {
        console.warn("Shop coordinates not available for centering:", {
          shop,
          hasLatitude: !!shop.latitude,
          hasLongitude: !!shop.longitude,
        });
        return;
      }

      // Turn on shop markers if not already enabled
      if (!mapOptions.showShops) {
        console.log("🔄 Turning on shop markers for centering");
        setMapOptions((prev) => ({ ...prev, showShops: true }));
      }

      console.log(
        "🎯 Centering on shop:",
        shop.name,
        "at",
        shop.latitude,
        shop.longitude
      );
      panToSingleCoordinate(
        { latitude: shop.latitude, longitude: shop.longitude },
        15
      );
    },
    [panToSingleCoordinate, mapOptions.showShops]
  );

  // Pan out to show all Google shops
  const handlePanOut = useCallback(() => {
    if (!googleShops || googleShops.length === 0) {
      console.warn("No Google shops available for panning out");
      return;
    }

    const shopCoordinates: Coordinate[] = googleShops
      .filter((shop) => shop.latitude && shop.longitude)
      .map((shop) => ({
        latitude: shop.latitude,
        longitude: shop.longitude,
      }));

    // Include vehicle location if available
    if (selectedVehicle?.lastLatitude && selectedVehicle?.lastLongitude) {
      shopCoordinates.push({
        latitude: selectedVehicle.lastLatitude,
        longitude: selectedVehicle.lastLongitude,
      });
    }

    if (shopCoordinates.length > 0) {
      console.log(
        "🗺️ Panning out to show all Google shops:",
        shopCoordinates.length
      );
      panToCoordinates(shopCoordinates, { padding: 100, maxZoom: 12 });
    }
  }, [googleShops, selectedVehicle, panToCoordinates]);

  // Center on selected vehicle
  const handleCenterVehicle = useCallback(() => {
    if (!selectedVehicle?.lastLatitude || !selectedVehicle?.lastLongitude) {
      console.warn("Vehicle coordinates not available for centering");
      return;
    }

    console.log("🎯 Centering on vehicle:", selectedVehicle.name);
    panToSingleCoordinate(
      {
        latitude: selectedVehicle.lastLatitude,
        longitude: selectedVehicle.lastLongitude,
      },
      vehicleFocusZoom
    );
  }, [selectedVehicle, panToSingleCoordinate]);

  useVehicleMarkers({
    map: map || null,
    vehicles,
    selectedVehicleId,
    onVehicleClick: handleVehicleClick,
    clustersEnabled: mapOptions.showClusters,
    isVisible: mapOptions.showVehicles,
  });

  useShopMarkers({
    map: map || null,
    showShops: mapOptions.showShops,
    searchQuery: shopSearchQuery,
    googleShops: googleShops,
    onShopClick: handleShopClick,
    activeShopId: selectedShopId,
  });

  const searchableShops = useMemo(
    () => [...allShops.filter((shop) => shop.isActive), ...googleShops],
    [allShops, googleShops]
  );

  const handleShopSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        if (map) {
          map.setZoom(defaultZoom);
          map.setCenter(defaultCenter);
        }
      }
      setShopSearchQuery(query);
    },
    [map]
  );

  const { isLoading: isHistoryLoading, fitRouteBounds } = useVehicleHistory({
    map: map || null,
    selectedVehicleId,
    selectedVehicle,
    token,
    defaultCenter,
    defaultZoom,
    showRoute: mapOptions.showRoute,
    onClose: () => setSelectedVehicleId(undefined),
  });

  // Pan out to show vehicle route
  const handlePanOutRoute = useCallback(() => {
    if (!selectedVehicle?.lastLatitude || !selectedVehicle?.lastLongitude) {
      console.warn("Vehicle coordinates not available for panning out route");
      return;
    }

    console.log("🗺️ Panning out to show vehicle route:", selectedVehicle.name);

    // Use the same bounds fitting logic as route display
    if (fitRouteBounds) {
      fitRouteBounds();
    } else {
      // Fallback to centering on vehicle if no route bounds available
      panToSingleCoordinate(
        {
          latitude: selectedVehicle.lastLatitude,
          longitude: selectedVehicle.lastLongitude,
        },
        10
      );
    }
  }, [selectedVehicle, fitRouteBounds, panToSingleCoordinate]);

  const { toggleTraffic } = useTrafficLayer({
    map: map || null,
    showTraffic: mapOptions.showTraffic,
  });

  // Always fetch AnyTrack locations (poll every 60 s); map markers are toggled separately
  const { data: anyTrackLocations = [], isLoading: isAnyTrackLoading } =
    useGetAnyTrackLocationsQuery(undefined, { pollingInterval: 60_000 });
  const selectedTrailer = anyTrackLocations.find(
    (location) => location.deviceId === selectedTrailerId
  );

  const handleTrailerClick = useCallback(
    (deviceId: string) => {
      setSelectedTrailerId(deviceId);
      setSelectedVehicleId(undefined);
      setShowTooltips(false);
      // Always make trailer pins visible so the tooltip and marker are shown
      setMapOptions((prev) =>
        prev.showAnyTrackVehicles ? prev : { ...prev, showAnyTrackVehicles: true }
      );
      const location = anyTrackLocations.find((l) => l.deviceId === deviceId);
      if (location?.latitude != null && location?.longitude != null && map) {
        panToCoordinate(map, { latitude: location.latitude, longitude: location.longitude }, 14);
      }
    },
    [anyTrackLocations, map]
  );

  const handleCenterTrailer = useCallback(() => {
    if (selectedTrailer?.latitude == null || selectedTrailer.longitude == null || !map) {
      console.warn("Trailer coordinates not available for centering");
      return;
    }

    panToCoordinate(
      map,
      { latitude: selectedTrailer.latitude, longitude: selectedTrailer.longitude },
      14
    );
  }, [map, selectedTrailer]);

  const handleCloseTrailerTooltip = useCallback(() => {
    setSelectedTrailerId(undefined);
  }, []);

  useAnyTrackMarkers({
    map: map || null,
    locations: anyTrackLocations,
    isVisible: mapOptions.showAnyTrackVehicles,
    searchQuery: anyTrackSearchQuery,
    selectedTrailerId,
    onTrailerClick: handleTrailerClick,
  });

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const handleMapTypeChange = useCallback(
    (type: MapType) => {
      setMapOptions((prev) => ({ ...prev, currentMapType: type }));
      setMapType(type as google.maps.MapTypeId);
    },
    [setMapType]
  );

  const handleFullscreenToggle = useCallback(() => {
    if (!containerRef.current) return;

    toggleFullscreen(containerRef.current);
    setMapOptions((prev) => {
      const newIsFullscreen = !prev.isFullscreen;
      return { ...prev, isFullscreen: newIsFullscreen };
    });
  }, [toggleFullscreen]);

  const handleClustersToggle = useCallback(() => {
    setMapOptions((prev) => ({ ...prev, showClusters: !prev.showClusters }));
  }, []);

  const handleVehiclePinsToggle = useCallback(() => {
    setMapOptions((prev) => ({ ...prev, showVehicles: !prev.showVehicles }));
  }, []);

  const handleShopsToggle = useCallback(() => {
    const newShowShops = !mapOptions.showShops;
    console.log("🔄 Toggle clicked:", {
      current: mapOptions.showShops,
      new: newShowShops,
    });

    setMapOptions((prev) => ({ ...prev, showShops: newShowShops }));

    // Clear Google shops when turning off shop markers
    if (!newShowShops) {
      console.log(
        "🧹 Clearing Google shops - current count:",
        googleShops.length
      );
      setGoogleShops([]);
      setShopSearchQuery("");
    }
  }, [mapOptions.showShops, googleShops.length]);

  const handleTrafficToggle = useCallback(() => {
    setMapOptions((prev) => ({
      ...prev,
      showTraffic: !prev.showTraffic,
    }));
    toggleTraffic();
  }, [toggleTraffic]);

  const handleTimeZonesToggle = useCallback(() => {
    setMapOptions((prev) => ({
      ...prev,
      showTimeZones: !prev.showTimeZones,
    }));
  }, []);

  const handleRouteToggle = useCallback(() => {
    setMapOptions((prev) => ({
      ...prev,
      showRoute: !prev.showRoute,
    }));
  }, []);

  const handleAnyTrackToggle = useCallback(() => {
    setMapOptions((prev) => ({
      ...prev,
      showAnyTrackVehicles: !prev.showAnyTrackVehicles,
    }));
    setAnyTrackSearchQuery("");
  }, []);

  const handleWeatherLayerChange = useCallback(
    (index: number, option: WeatherLayerOption) => {
      setMapOptions((prev) => ({
        ...prev,
        weatherLayers: prev.weatherLayers.map((layer, i) => {
          if (i === index) {
            return option;
          }
          // If the selected layer is being turned on, turn off all others
          if (option.isVisible) {
            return { ...layer, isVisible: false };
          }
          return layer;
        }),
      }));
    },
    []
  );

  const handleZoomSensitivityChange = useCallback((sensitivity: number) => {
    setMapOptions((prev) => ({ ...prev, zoomSensitivity: sensitivity }));
  }, []);

  if (loadError || vehiclesError) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "error.main",
        }}
      >
        {loadError ? "Error loading map" : "Error loading vehicles"}
      </Box>
    );
  }

  if (!isLoaded) {
    return <LoadingSpinner fullHeight />;
  }

  return (
    <Box ref={containerRef} sx={{ position: "relative", height: "100%" }}>
      <GoogleMap
        key={key}
        mapContainerStyle={mapContainerStyle}
        center={defaultCenter}
        zoom={defaultZoom}
        options={{
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          cameraControl: false,
          rotateControl: false,
          mapTypeId: mapOptions.currentMapType,
          mapId: process.env.REACT_APP_GOOGLE_MAPS_ID || "",
          colorScheme: theme.palette.mode === "dark" ? "DARK" : "LIGHT",
          gestureHandling: "cooperative",
          scrollwheel: false,
        }}
        onLoad={onMapLoad}
      >
        {(mapOptions.showShops || mapOptions.showAnyTrackVehicles) && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 2000,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: 1,
              maxWidth: "calc(100vw - 32px)",
              flexWrap: "wrap",
            }}
          >
            {mapOptions.showShops && (
              <ShopSearchControl
                onSearch={handleShopSearch}
                results={searchableShops}
                onResultSelect={handleShopClick}
                onClose={handleShopsToggle}
              />
            )}

            {mapOptions.showAnyTrackVehicles && (
              <AnyTrackSearchControl
                onSearch={setAnyTrackSearchQuery}
                results={anyTrackLocations}
                onResultSelect={handleTrailerClick}
                onClose={handleAnyTrackToggle}
              />
            )}
          </Box>
        )}

        <MapControls
          options={mapOptions}
          onMapTypeChange={handleMapTypeChange}
          onFullscreenToggle={handleFullscreenToggle}
          onClustersToggle={handleClustersToggle}
          onVehiclePinsToggle={handleVehiclePinsToggle}
          onShopsToggle={handleShopsToggle}
          onTrafficToggle={handleTrafficToggle}
          onTimeZonesToggle={handleTimeZonesToggle}
          onAnyTrackToggle={handleAnyTrackToggle}
          onWeatherLayerChange={handleWeatherLayerChange}
          onZoomSensitivityChange={handleZoomSensitivityChange}
        />

        <Tooltip title="Show all vehicles" placement="left">
          <IconButton
            onClick={handleShowAllVehicles}
            aria-label="Show all vehicles"
            sx={{
              position: "absolute",
              bottom: 40,
              right: 8,
              zIndex: 1000,
              backgroundColor:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(33, 33, 33, 0.9)",
              color: theme.palette.text.primary,
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              width: 34,
              height: 34,
              "& .MuiSvgIcon-root": {
                fontSize: 20,
              },
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "light"
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(33, 33, 33, 0.8)",
              },
            }}
          >
            <ZoomOutMapIcon />
          </IconButton>
        </Tooltip>

        {/* SignalR Connection Indicator */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 70, // Position next to MapControls (220px = MapControls width + margin)
            zIndex: 1000,
          }}
        >
          <SignalRIndicator />
        </Box>

        <WeatherOverlay
          map={map || null}
          weatherLayers={mapOptions.weatherLayers}
        />
        {map && (
          <TimeZonesOverlay
            map={map || null}
            isVisible={mapOptions.showTimeZones}
          />
        )}
        <ViolationsOverlay vehicles={vehicles} hosProfiles={profiles} />
      </GoogleMap>
      <VehicleSidebar
        vehicles={vehicles}
        selectedVehicleId={selectedVehicleId}
        onVehicleSelect={handleVehicleClick}
        isLoading={isVehiclesLoading}
        showVehicles={mapOptions.showVehicles}
        onShowVehiclesToggle={handleVehiclePinsToggle}
        onSidebarToggle={setIsSidebarOpen}
        defaultOpen={!isLaptopScreen}
      />
      <TrailerSidebar
        trailers={anyTrackLocations}
        selectedTrailerId={selectedTrailerId}
        onTrailerSelect={handleTrailerClick}
        isLoading={isAnyTrackLoading}
        isAnyTrackEnabled={true}
        showTrailers={mapOptions.showAnyTrackVehicles}
        onShowTrailersToggle={handleAnyTrackToggle}
        vehicleSidebarOpen={isSidebarOpen}
        onSidebarToggle={setIsTrailerSidebarOpen}
        defaultOpen={!isLaptopScreen}
      />

      {/* Map Tooltips Row - positioned at top, next to sidebar */}
      {showTooltips && selectedVehicle && (
        <MapTooltipsRow
          selectedVehicle={selectedVehicle}
          hosProfile={selectedHosProfile}
          isSidebarOpen={isSidebarOpen}
          trailerSidebarOpen={isTrailerSidebarOpen}
          closestShops={closestShops}
          isShopsLoading={isShopsLoading}
          isCalculatingDriving={isCalculatingDriving}
          shopsError={shopsError}
          onClose={handleCloseTooltips}
          onGoogleShopSelect={handleGoogleShopSelect}
          showShops={mapOptions.showShops}
          showRoute={mapOptions.showRoute}
          onShopsToggle={() =>
            setMapOptions((prev) => ({ ...prev, showShops: !prev.showShops }))
          }
          onRouteToggle={handleRouteToggle}
          onPanToShops={handlePanToShops}
          onCenterShop={handleCenterShop}
          onPanOut={handlePanOut}
          onCenterVehicle={handleCenterVehicle}
          onPanOutRoute={handlePanOutRoute}
          isRouteLoading={isHistoryLoading}
        />
      )}

      {mapOptions.showAnyTrackVehicles && selectedTrailer && (
        <TrailerTooltip
          selectedTrailer={selectedTrailer}
          isSidebarOpen={isSidebarOpen}
          trailerSidebarOpen={isTrailerSidebarOpen}
          onClose={handleCloseTrailerTooltip}
          onCenterTrailer={handleCenterTrailer}
        />
      )}

      <Fade in={isHistoryLoading && mapOptions.showRoute} unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            bottom: 88,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 2,
            pointerEvents: "none",
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress size={40} thickness={4} />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RouteIcon sx={{ fontSize: "1.35rem", color: "primary.main" }} />
            </Box>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight="medium"
          >
            Loading route…
          </Typography>
        </Paper>
      </Fade>
    </Box>
  );
};

export default FleetMap;
